import React, { forwardRef, ForwardRefRenderFunction, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { hasItems } from "../../utils/collections";
import Button from "./Button";

const fullKeyName = (prefix: string, property: string): string => `${prefix}::${property}`;

export const clearDraft = (name: string): void => {
  if (!name) {
    return;
  }
  const keyPrefix = fullKeyName(name, "").toLowerCase();
  Object
    .entries(localStorage)
    .map(x => x[0])
    .filter(x => x.toLowerCase().startsWith(keyPrefix))
    .forEach(x => localStorage.removeItem(x));
  localStorage.removeItem(`${name}::tid`);
}

export interface DraftHandles {
  focus: () => void;
  isVisible: () => boolean;
}

export interface DraftProps {
  canShow: boolean;
  name: string;
  fieldNames: string[];
  data: Map<string, string>;
  label: string;
  trackingId?: string;
  setNewNodeTrackingId?: (trackingId: string) => void;
  onDraftLoaded: (data: Map<string, string>) => void;
  onDraftDeleted?: () => void;
}

const Draft: ForwardRefRenderFunction<DraftHandles, DraftProps> = (props: DraftProps, ref): JSX.Element => {
  const [initialDraftValue, setInitialDraftValue] = useState<Map<string, string>>(undefined);
  const [trackingId, setTrackingId] = useState<string>(props.trackingId);
  const refLoadBtn = useRef<HTMLButtonElement>();
  const refDialogHeading = useRef<HTMLDivElement>();

  useEffect(() => {
    if (props.canShow) {
      setTimeout(() => {
        if (refDialogHeading.current) {
          refDialogHeading.current.focus();
        }
      }, 10);
    }
  }, [props.canShow]);
  useEffect(() => {
    if (props.name && hasItems(props.fieldNames)) {
      const data: Map<string, string> = new Map<string, string>();
      let foundDraft: boolean = false;
      const storedTrackingId = localStorage.getItem(`${props.name}::tid`);
      if (storedTrackingId) {
        setTrackingId(storedTrackingId);
        if (props.setNewNodeTrackingId) {
          props.setNewNodeTrackingId(storedTrackingId);
        }
      } else if (props.trackingId) {
        localStorage.setItem(`${props.name}::tid`, props.trackingId);
      }

      props.fieldNames.forEach(key => {
        const draft = localStorage.getItem(fullKeyName(props.name, key)) ?? null;
        if (draft) {
          data.set(key, draft);
          foundDraft = true;
        }
      });

      if (foundDraft) {
        setInitialDraftValue(data);
      } else {
        setInitialDraftValue(null);
      }
    }
  }, [props.name, props.trackingId]);

  useEffect(() => {
    if (props.name && props.data && initialDraftValue !== undefined) {
      if (trackingId && !localStorage.getItem(`${props.name}::tid`)) {
        localStorage.setItem(`${props.name}::tid`, trackingId);
      }
      props.fieldNames.forEach((key) => {
        localStorage.setItem(fullKeyName(props.name, key), props.data.get(key));
      });
    }
  }, [props.name, props.data, trackingId]);

  const loadDraftClicked = (): void => {
    props.onDraftLoaded(initialDraftValue);
    setInitialDraftValue(null);
  }

  const deleteDraftClicked = (): void => {
    props.fieldNames.forEach((key) => {
      localStorage.removeItem(fullKeyName(props.name, key));
    });
    localStorage.removeItem(`${props.name}::tid`);
    setInitialDraftValue(null);
    props.onDraftDeleted?.();
  }

  if (!props.name || !initialDraftValue) {
    return null;
  }

  return <div>
    {props.canShow && <Wrapper role="dialog" tabIndex={-1} ref={refDialogHeading}>
      <Mask />
      <Content>
        <div>We noticed that you were previously working on {props.label}. What would you like to do now?</div>
        <div className="buttons">
          <Button buttonType="normal" size="semiCompact" onClick={deleteDraftClicked}>Delete Draft</Button>
          <Button ref={refLoadBtn} size="semiCompact" onClick={loadDraftClicked}>Load Draft</Button>
        </div>
      </Content>
    </Wrapper>}
  </div>;
}

export default forwardRef(Draft);

const Wrapper = styled.div`
  .buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 2rem;

    >* + * {
      margin-left: 2rem;
    }

    button {
      font-size: 1.6rem;
      padding: .5rem 1.5rem;
    }
  }
`;

const Mask = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0px;
  left: 0px;
  background-color: white;
  opacity: 0.6;
  z-index: 90;
`;

const Content = styled.div`
  position: relative;
  border: solid 1px var(--color-primary);
  border-radius: 2px;
  padding: 1.5rem 2rem;
  background-color: #edf7ff;
  z-index: 90;
`;